/**
 * useEventStream - SSE(Server-Sent Events) 실시간 연결 훅
 *
 * 앱 최상위에서 1개 연결을 유지하며, 채팅/알림 이벤트를 수신합니다.
 * 브라우저 탭이 숨겨지면 연결을 해제하고, 복귀 시 재연결합니다.
 *
 * 사용법:
 *   const { lastChatMessage, lastReadEvent, isConnected } = useEventStream();
 */

import { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
// /api 제거 후 /api/events/stream/ 구성
const SSE_BASE = API_BASE.replace(/\/api\/?$/, '');

export interface ChatMessageEvent {
  conversation_id: number;
  message: {
    id: number;
    sender: { id: number; nickname: string; profile_image: string };
    message_type: string;
    content: string;
    image?: string;
    is_read: boolean;
    created_at: string;
  };
}

export interface MessageReadEvent {
  conversation_id: number;
}

export interface NotificationEvent {
  id: number;
  type: string;
  title: string;
  content: string;
}

// 글로벌 이벤트 리스너 패턴 (여러 컴포넌트에서 구독)
type EventCallback<T = any> = (data: T) => void;

class EventStreamManager {
  private source: EventSource | null = null;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private reconnectTimer: number | null = null;
  private token: string | null = null;
  private sellerId: string | null = null;
  private _isConnected = false;
  private _onConnectionChange: ((connected: boolean) => void) | null = null;

  connect(token: string, sellerId?: string | null) {
    // 이미 같은 토큰으로 연결 중이면 무시
    if (this.source && this.token === token && this.sellerId === (sellerId || null)) {
      return;
    }

    this.disconnect();
    this.token = token;
    this.sellerId = sellerId || null;

    let url = `${SSE_BASE}/api/events/stream/?token=${token}`;
    if (sellerId) {
      url += `&seller_id=${sellerId}`;
    }

    try {
      this.source = new EventSource(url);

      this.source.addEventListener('connected', () => {
        this._isConnected = true;
        this._onConnectionChange?.(true);
      });

      this.source.addEventListener('chat_message', (e: MessageEvent) => {
        this._dispatch('chat_message', JSON.parse(e.data));
      });

      this.source.addEventListener('message_read', (e: MessageEvent) => {
        this._dispatch('message_read', JSON.parse(e.data));
      });

      this.source.addEventListener('notification', (e: MessageEvent) => {
        this._dispatch('notification', JSON.parse(e.data));
      });

      this.source.onerror = () => {
        this._isConnected = false;
        this._onConnectionChange?.(false);
        // EventSource는 자동 재연결하지만, 연결 상태 추적
      };
    } catch (err) {
      console.warn('[SSE] 연결 실패:', err);
    }
  }

  disconnect() {
    if (this.source) {
      this.source.close();
      this.source = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._isConnected = false;
    this._onConnectionChange?.(false);
    this.token = null;
    this.sellerId = null;
  }

  on<T = any>(event: string, callback: EventCallback<T>) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback);
  }

  off<T = any>(event: string, callback: EventCallback<T>) {
    this.listeners.get(event)?.delete(callback as EventCallback);
  }

  get isConnected() {
    return this._isConnected;
  }

  set onConnectionChange(cb: ((connected: boolean) => void) | null) {
    this._onConnectionChange = cb;
  }

  private _dispatch(event: string, data: any) {
    this.listeners.get(event)?.forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error(`[SSE] ${event} handler error:`, err);
      }
    });
  }
}

// 싱글톤 인스턴스
export const eventStream = new EventStreamManager();

/**
 * SSE 연결을 관리하는 최상위 훅.
 * App.tsx에서 한 번만 호출합니다.
 */
export function useEventStreamInit(
  token: string | null | undefined,
  sellerId?: string | number | null
) {
  useEffect(() => {
    if (!token) {
      eventStream.disconnect();
      return;
    }

    const sellerStr = sellerId ? String(sellerId) : null;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        eventStream.connect(token, sellerStr);
      } else {
        eventStream.disconnect();
      }
    };

    // 초기 연결
    eventStream.connect(token, sellerStr);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      eventStream.disconnect();
    };
  }, [token, sellerId]);
}

/**
 * 특정 이벤트를 구독하는 훅.
 * Chat.tsx, ChatModal.tsx 등에서 사용합니다.
 */
export function useSSEEvent<T = any>(
  eventName: string,
  callback: EventCallback<T>,
  deps: any[] = []
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler: EventCallback<T> = (data) => callbackRef.current(data);
    eventStream.on(eventName, handler);
    return () => {
      eventStream.off(eventName, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, ...deps]);
}

/**
 * SSE 연결 상태를 반환하는 훅.
 */
export function useSSEConnectionStatus(): boolean {
  const [connected, setConnected] = useState(eventStream.isConnected);

  useEffect(() => {
    eventStream.onConnectionChange = setConnected;
    return () => {
      eventStream.onConnectionChange = null;
    };
  }, []);

  return connected;
}
